// La ventana «Santi» (propuesta del 03-11, docs/pelotas/ux-2026-11-03/propuesta/propuesta.md §5; P1, P3, P4, P6, P8):
// dos columnas encima de la oficina velada — la lista (360 px; 320 por debajo de 1280) y lo abierto (un contrato, el chat
// con Santi o «Pedir algo a Santi»). «Tu agente» es un cajón de 380 px a demanda: empuja la conversación en escritorio y
// la tapa en el iPad. Esc o ✕ la cierran (Esc cierra antes el cajón si está abierto). Por debajo de 768 (el móvil, que
// no se diseña pero no se rompe) va una columna cada vez.
// «Hecho» y «Cancelar» (§5.6): un clic, el contrato sale de la lista, el siguiente queda abierto y abajo sale la línea
// de 8 s con «Deshacer»; lo que va al centro sale cuando se va la línea, o al cerrar la ventana (store `marcar`). Si no
// llega, el aviso se queda arriba de la ventana con «Volver a probar» (AvisosActo), también la próxima vez que se abra.
// 02-11 (Pau: los socios ven y hacen todo en la oficina): la ventana se abre para cualquier sesión; a un socio no le
// salen las cosas que salen hacia Santi como Pau (useHablaComoPau).
import { useCallback, useEffect, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { AvisosActo } from './AvisosActo';
import { CharlaSanti } from './CharlaSanti';
import { LineaActo } from './LineaActo';
import { ListaSanti, seccionesDe } from './ListaSanti';
import { PedirAlgo } from './PedirAlgo';
import { pedirHilo, recordarUltimo, useHablaComoPau, usePelotasStore } from './store';
import type { Relleno } from './TarjetaParaSanti';
import { TuAgente } from './TuAgente';
import type { ContratoDTO, HiloAbierto, MotivoCierre } from './tipos';

interface PropsVentanaSanti {
  hilo: HiloAbierto;
  alCerrar: () => void;
  alAbrirHilo: (hilo: HiloAbierto) => void;
  alAbrirAgente: (agentId: string) => void;
}

export function VentanaSanti({ hilo: pedido, alCerrar, alAbrirHilo, alAbrirAgente }: PropsVentanaSanti) {
  const comoPau = useHablaComoPau();
  // «Pedir algo» es de Pau: a un socio, el chat con Santi en su lugar (nunca una pantalla que no puede usar).
  const hilo: HiloAbierto = pedido === 'pedir' && !comoPau ? 'general' : pedido;
  const lista = usePelotasStore((s) => s.lista);
  const errorLista = usePelotasStore((s) => s.errorLista);
  const cargarLista = usePelotasStore((s) => s.cargarLista);
  const datos = usePelotasStore((s) => (typeof hilo === 'number' ? s.hilos[hilo] : undefined));
  const general = usePelotasStore((s) => s.general);
  const marcar = usePelotasStore((s) => s.marcar);
  const deshacerActo = usePelotasStore((s) => s.deshacerActo);
  const lineas = usePelotasStore((s) => (s.acto ? 1 : 0));
  const ancha = useMediaQuery('(min-width: 1280px)');
  const movil = !useMediaQuery('(min-width: 768px)');
  const [vista, setVista] = useState<'lista' | 'charla'>('charla');
  const [cajon, setCajon] = useState(false);
  // Lo que «Usar en el chat» deja en la caja, atado al contrato de la tarjeta (en otro hilo no se aplica nunca).
  const [relleno, setRelleno] = useState<{ n: number; texto: string; hilo: HiloAbierto } | null>(null);

  useEffect(() => {
    setVista('charla');
    recordarUltimo(hilo);
  }, [hilo]);
  useEffect(() => {
    if (!lista) void cargarLista();
    pedirHilo(hilo);
  }, [hilo, lista, cargarLista]);
  // Cerrar la ventana (o salir de la oficina) confirma lo pendiente: la línea se va con ella.
  useEffect(() => () => usePelotasStore.getState().confirmarActo(), []);

  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"]:not([data-ventana-santi]), [role="menu"]')) return;
      const destino = e.target as HTMLElement | null;
      const escribiendo = !!destino && (destino.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(destino.tagName));
      if (escribiendo && (destino as HTMLInputElement).value) return;
      if (cajon) setCajon(false);
      else alCerrar();
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [alCerrar, cajon]);

  const abrir = useCallback(
    (h: HiloAbierto) => {
      alAbrirHilo(h);
      setVista('charla');
    },
    [alAbrirHilo],
  );

  /** «Hecho» / «Cancelar»: fuera de la lista y el siguiente queda abierto (el de debajo; si era el último, el de arriba). */
  const alMarcar = useCallback(
    (c: ContratoDTO, motivo: MotivoCierre) => {
      const { teToca, esperando } = seccionesDe(usePelotasStore.getState().lista, usePelotasStore.getState().quitados);
      const orden = [...teToca, ...esperando];
      const i = orden.findIndex((x) => x.temaId === c.temaId);
      const siguiente = i < 0 ? orden[0] : (orden[i + 1] ?? orden[i - 1]);
      marcar(c, motivo);
      alAbrirHilo(siguiente && siguiente.temaId !== c.temaId ? siguiente.temaId : 'general');
    },
    [marcar, alAbrirHilo],
  );
  const alDeshacer = useCallback(() => {
    const temaId = deshacerActo();
    if (temaId !== null) alAbrirHilo(temaId);
  }, [deshacerActo, alAbrirHilo]);

  const alUsar = useCallback(
    (r: Relleno, temaId: number | null) => {
      const destino: HiloAbierto = temaId ?? 'general';
      setRelleno((v) => ({ n: (v?.n ?? 0) + 1, texto: r.texto, hilo: destino }));
      if (destino !== hilo) alAbrirHilo(destino);
      if (!ancha) setCajon(false);
    },
    [hilo, ancha, alAbrirHilo],
  );

  const soltarRelleno = useCallback(() => setRelleno(null), []);
  const cajonEncima = !ancha;
  const nodoCajon = cajon ? <TuAgente alUsar={alUsar} alCerrar={() => setCajon(false)} /> : null;
  const linea = <LineaActo alDeshacer={alDeshacer} />;
  const comunes = {
    ancha,
    cajon: nodoCajon,
    cajonAbierto: cajon,
    cajonEncima,
    linea,
    alCajon: () => setCajon((v) => !v),
    alCerrar,
    alVerLista: movil ? () => setVista('lista') : undefined,
  };

  return (
    <div className="fixed inset-0 z-40" data-testid="ventana-santi">
      <button type="button" tabIndex={-1} aria-label={t('oficinaV2.pelotas.ventana.cerrar')} onClick={alCerrar} className="absolute inset-0 cursor-default bg-black/55 animate-in fade-in-0" />
      <section
        role="dialog"
        aria-label={t('oficinaV2.pelotas.ventana.titulo')}
        data-ventana-santi="true"
        className={cn(
          'absolute flex flex-col overflow-hidden bg-oficina-fondo text-oficina-texto shadow-2xl shadow-black/60 ring-1 ring-white/[0.06] animate-in fade-in-0 zoom-in-[0.98]',
          movil ? 'inset-0' : 'inset-4 rounded-2xl',
        )}
      >
        <AvisosActo />
        <div className={cn('grid min-h-0 flex-1', movil ? 'grid-cols-1' : ancha ? 'grid-cols-[360px_minmax(0,1fr)]' : 'grid-cols-[320px_minmax(0,1fr)]')}>
          {movil && vista !== 'lista' ? null : <ListaSanti lista={lista} errorLista={errorLista} activo={hilo} alAbrir={abrir} corto={!ancha} />}
          {movil && vista !== 'charla' ? null : hilo === 'pedir' ? (
            <PedirAlgo {...comunes} alPedido={(temaId) => alAbrirHilo(temaId)} />
          ) : (
            <CharlaSanti
              key={String(hilo)}
              {...comunes}
              hilo={hilo}
              datos={datos}
              general={general}
              lista={lista}
              relleno={relleno && relleno.hilo === hilo ? { n: relleno.n, texto: relleno.texto } : null}
              alRellenado={soltarRelleno}
              lineas={lineas}
              alMarcar={alMarcar}
              alPedirAlgo={() => alAbrirHilo('pedir')}
              alAbrirAgente={alAbrirAgente}
            />
          )}
        </div>
      </section>
    </div>
  );
}
